// components/ChatbotWidget.jsx - Fixed Undo Changes functionality
import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import documentService from '../services/documentService';
import useApiClient from '../services/api';

const ChatbotWidget = ({ 
  initialHtml = '', 
  documentId = null, 
  onHtmlUpdate = null,
  enabled = true,
  position = 'bottom-right',
  apiEndpoint = '/documents',
  title = 'AI Assistant',
  welcomeMessage = 'Hi! How can I help you improve this content?',
  selectedText = '',
  selectionContext = '',
  onProcessMessage = null,
  isEmbedded = false
}) => {
  const [isOpen, setIsOpen] = useState(isEmbedded ? true : false);
  const [isMinimized, setIsMinimized] = useState(isEmbedded ? false : true);
  const [messages, setMessages] = useState([]);
  const [currentInput, setCurrentInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentHtml, setCurrentHtml] = useState(initialHtml);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const [showCommands, setShowCommands] = useState(false);
  
  // FIXED: Add HTML history stack for undo functionality
  const [htmlHistory, setHtmlHistory] = useState([initialHtml]);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(0);
  
  const apiClient = useApiClient();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const widgetRef = useRef(null);

  // Quick command definitions
  const quickCommands = [
    { 
      command: '/format', 
      description: 'Improve formatting and structure',
      icon: '📝'
    },
    { 
      command: '/grammar', 
      description: 'Fix grammar issues',
      icon: '✏️'
    },
    { 
      command: '/formal', 
      description: 'Make text more formal',
      icon: '🎩'
    },
    { 
      command: '/casual', 
      description: 'Make text more casual',
      icon: '😊'
    },
    { 
      command: '/retranslate', 
      description: 'Retranslate with better accuracy',
      icon: '🔄'
    },
    { 
      command: '/context', 
      description: 'Improve contextual translation',
      icon: '🎯'
    }
  ];

  // Initialize with welcome message when first opened
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        id: Date.now(),
        type: 'bot',
        content: welcomeMessage,
        timestamp: new Date()
      }]);
    }
  }, [isOpen, messages.length, welcomeMessage]);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);

  // FIXED: Update HTML history when initialHtml changes
  useEffect(() => {
    if (initialHtml !== currentHtml) {
      setCurrentHtml(initialHtml);
      // Update history if this is a new HTML state
      if (initialHtml && !htmlHistory.includes(initialHtml)) {
        setHtmlHistory(prev => [...prev, initialHtml]);
        setCurrentHistoryIndex(htmlHistory.length);
      }
    }
  }, [initialHtml, currentHtml, htmlHistory]);

  // Enhanced focus management
  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      const focusTimeout = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest' 
          });
        }
      }, 200);
      
      return () => clearTimeout(focusTimeout);
    }
  }, [isOpen, isMinimized, isLoading]);

  // Auto-focus after message processing
  useEffect(() => {
    if (!isLoading && isOpen && !isMinimized && inputRef.current) {
      const refocusTimeout = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
      
      return () => clearTimeout(refocusTimeout);
    }
  }, [isLoading, isOpen, isMinimized]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // FIXED: Enhanced addMessage function to store HTML state
  const addMessage = (type, content, metadata = {}) => {
    const newMessage = {
      id: Date.now() + Math.random(),
      type,
      content,
      timestamp: new Date(),
      ...metadata
    };
    
    // If this is a message with HTML changes, store the previous HTML state
    if (metadata.htmlUpdate && metadata.previousHtml) {
      newMessage.previousHtml = metadata.previousHtml;
      newMessage.newHtml = metadata.newHtml;
    }
    
    setMessages(prev => [...prev, newMessage]);
    
    if (type === 'bot' && (isMinimized || !isOpen)) {
      setHasNewMessage(true);
    }
    
    return newMessage;
  };

  // FIXED: Function to add HTML state to history
  const addToHtmlHistory = (newHtml) => {
    setHtmlHistory(prev => {
      // Remove any future history if we're not at the end
      const newHistory = prev.slice(0, currentHistoryIndex + 1);
      // Add new state
      newHistory.push(newHtml);
      // Limit history to last 20 states
      if (newHistory.length > 20) {
        newHistory.shift();
        setCurrentHistoryIndex(newHistory.length - 1);
        return newHistory;
      }
      setCurrentHistoryIndex(newHistory.length - 1);
      return newHistory;
    });
  };

  const toggleWidget = () => {
    if (isMinimized) {
      setIsMinimized(false);
      setIsOpen(true);
      setHasNewMessage(false);
    } else {
      setIsMinimized(true);
    }
  };

  const closeWidget = () => {
    setIsOpen(false);
    setIsMinimized(true);
  };

  const handleQuickCommand = (command) => {
    setCurrentInput(command);
    setShowCommands(false);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        const inputElement = inputRef.current;
        inputElement.setSelectionRange(inputElement.value.length, inputElement.value.length);
      }
    }, 50);
  };

  const handleSendMessage = async () => {
    if (!currentInput.trim() || isLoading || !documentId) return;
  
    let userMessage = currentInput.trim();
    const originalInput = currentInput.trim();
    
    // FIXED: Store current HTML before making changes
    const previousHtml = currentHtml;
    
    setCurrentInput('');
    setShowCommands(false);
  
    if (onProcessMessage) {
      userMessage = await onProcessMessage(userMessage, selectedText, selectionContext);
    }
  
    addMessage('user', originalInput);
  
    const loadingMessage = addMessage('bot', 'Processing your request...', { isLoading: true });
  
    setIsLoading(true);
  
    try {
      const requestPayload = {
        currentHtml: currentHtml,
        userFeedback: userMessage,
        selectedHtml: getSelectedHtmlFromEditor(),
        selectedText: selectedText || null,
        selectionContext: selectionContext || null,
        hasSelection: !!selectedText,
        isCommand: userMessage.startsWith('/'),
        commandType: userMessage.startsWith('/') ? userMessage.split(' ')[0].substring(1) : null,
        conversationHistory: messages.slice(-10)
      };
  
      const result = await documentService(apiClient).improveHTMLWithSelection(documentId, requestPayload);

      // Remove loading message
      setMessages(prev => prev.filter(msg => msg.id !== loadingMessage.id));

      if (result.success && result.improvedHtml) {
        // FIXED: Add to history before updating current HTML
        addToHtmlHistory(result.improvedHtml);
        setCurrentHtml(result.improvedHtml);
        
        if (onHtmlUpdate) {
          onHtmlUpdate(result.improvedHtml, result.changes);
        }
        
        // FIXED: Add success message with both previous and new HTML
        addMessage('bot', result.explanation || 'Content updated!', {
          htmlUpdate: true,
          changes: result.changes || [],
          previousHtml: previousHtml,
          newHtml: result.improvedHtml,
          selectedText: selectedText
        });
        
        toast.success('Content updated successfully!');
      } else {
        addMessage('bot', result.message || 'I couldn\'t process your request. Could you please be more specific?');
      }
  
    } catch (error) {
      console.error('Error improving HTML:', error);
      
      setMessages(prev => prev.filter(msg => msg.id !== loadingMessage.id));
      
      addMessage('bot', 'Sorry, I encountered an error while processing your request. Please try again.');
      toast.error('Failed to improve HTML');
    } finally {
      setIsLoading(false);
      
      setTimeout(() => {
        if (inputRef.current && isOpen && !isMinimized) {
          inputRef.current.focus();
        }
      }, 100);
    }
  };
  
  const getSelectedHtmlFromEditor = () => {
    if (window.tinymce && window.tinymce.activeEditor) {
      const editor = window.tinymce.activeEditor;
      const selection = editor.selection;
      
      if (selection && selectedText) {
        const selectedHtml = selection.getContent({ format: 'html' });
        console.log('Selected HTML:', selectedHtml);
        return selectedHtml;
      }
    }
    
    return null;
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    } else if (e.key === '/' && currentInput === '') {
      e.preventDefault();
      setShowCommands(true);
    } else if (e.key === 'Escape') {
      setShowCommands(false);
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 10);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setCurrentInput(value);
    
    if (value === '/') {
      setShowCommands(true);
    } else if (!value.startsWith('/')) {
      setShowCommands(false);
    }
  };

  // FIXED: Improved handleRevert function
  const handleRevert = (messageId) => {
    const message = messages.find(msg => msg.id === messageId);
    if (message && message.previousHtml) {
      // Update current HTML
      setCurrentHtml(message.previousHtml);
      
      // Update HTML history
      addToHtmlHistory(message.previousHtml);
      
      // Notify parent component
      if (onHtmlUpdate) {
        onHtmlUpdate(message.previousHtml, ['Reverted to previous version']);
      }
      
      // Add a new message indicating the revert
      addMessage('bot', 'Reverted to previous version successfully.', {
        htmlUpdate: true,
        changes: ['Reverted to previous version'],
        previousHtml: currentHtml,
        newHtml: message.previousHtml
      });
      
      toast.info('Reverted to previous version');
    } else {
      // Fallback: try to revert using HTML history
      if (currentHistoryIndex > 0) {
        const previousIndex = currentHistoryIndex - 1;
        const previousHtml = htmlHistory[previousIndex];
        
        if (previousHtml) {
          setCurrentHtml(previousHtml);
          setCurrentHistoryIndex(previousIndex);
          
          if (onHtmlUpdate) {
            onHtmlUpdate(previousHtml, ['Reverted to previous version']);
          }
          
          addMessage('bot', 'Reverted to previous version successfully.', {
            htmlUpdate: true,
            changes: ['Reverted to previous version']
          });
          
          toast.info('Reverted to previous version');
        } else {
          toast.warning('No previous version available to revert to');
        }
      } else {
        toast.warning('No previous version available to revert to');
      }
    }
  };

  // FIXED: Add general undo function (can be triggered by keyboard shortcut)
  const handleUndo = () => {
    if (currentHistoryIndex > 0) {
      const previousIndex = currentHistoryIndex - 1;
      const previousHtml = htmlHistory[previousIndex];
      
      if (previousHtml) {
        setCurrentHtml(previousHtml);
        setCurrentHistoryIndex(previousIndex);
        
        if (onHtmlUpdate) {
          onHtmlUpdate(previousHtml, ['Undid last change']);
        }
        
        toast.info('Undid last change');
      }
    } else {
      toast.warning('Nothing to undo');
    }
  };

  // FIXED: Add keyboard shortcut for undo (Ctrl+Z)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 'z' && !e.shiftKey && isOpen && !isMinimized) {
        e.preventDefault();
        handleUndo();
      }
    };

    if (isOpen && !isMinimized) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isMinimized, currentHistoryIndex, htmlHistory]);

  const formatTimestamp = (timestamp) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getPositionClasses = () => {
    if (isEmbedded) {
      return 'w-full h-full max-w-none max-h-none relative';
    }
    
    const baseClasses = 'fixed z-50 flex flex-col';
    switch (position) {
      case 'bottom-left':
        return `${baseClasses} bottom-4 left-4 w-96 max-h-98`;
      case 'top-right':
        return `${baseClasses} top-4 right-4 w-96 max-h-98`;
      case 'top-left':
        return `${baseClasses} top-4 left-4 w-96 max-h-98`;
      default:
        return `${baseClasses} bottom-6 right-6 max-h-98`;
    }
  };

  if (!enabled || !documentId) return null;

  // For embedded mode, always show the full interface
  if (isEmbedded) {
    return (
      <div ref={widgetRef} className={getPositionClasses()}>
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg flex flex-col h-full">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message) => (
              <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                  message.type === 'user' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {message.isLoading ? (
                    <div className="flex items-center space-x-2">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                      <span className="text-sm">Processing...</span>
                    </div>
                  ) : (
                    <>
                      <p className="whitespace-pre-wrap">{message.content}</p>
                      
                      {message.htmlUpdate && message.changes && message.changes.length > 0 && (
                        <div className="mt-3 p-3 bg-white bg-opacity-20 rounded-md">
                          <h5 className="font-semibold text-sm mb-2">Changes made:</h5>
                          <ul className="text-sm space-y-1">
                            {message.changes.map((change, index) => (
                              <li key={index} className="flex items-start">
                                <span className="text-green-600 mr-1">•</span>
                                {change}
                              </li>
                            ))}
                          </ul>
                          {message.selectedText && (
                            <div className="mt-2 p-2 bg-white bg-opacity-30 rounded text-sm">
                              <strong>Focused on:</strong> "{message.selectedText.substring(0, 50)}..."
                            </div>
                          )}
                          {/* FIXED: Only show undo button if we have previous HTML or history */}
                          {(message.previousHtml || currentHistoryIndex > 0) && (
                            <button 
                              className="mt-2 px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
                              onClick={() => handleRevert(message.id)}
                              title="Undo this change"
                            >
                              Undo Changes
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {formatTimestamp(message.timestamp)}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Selection Info Panel for embedded mode */}
          {selectedText && (
            <div className="border-t border-gray-200 p-4 bg-blue-50">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <span className="text-lg">✏️</span>
                  <span className="font-medium text-gray-700">Selected Text</span>
                </div>
                <span className="text-sm text-gray-500">({selectedText.length} chars)</span>
              </div>
              <div className="bg-white p-3 rounded-md text-sm text-gray-700 mb-3 italic">
                "{selectedText.substring(0, 100)}{selectedText.length > 100 ? '...' : ''}"
              </div>
              <div className="flex space-x-2">
                <button 
                  className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors"
                  onClick={() => handleQuickCommand('/grammar')}
                >
                  ✏️ Grammar
                </button>
                <button 
                  className="px-3 py-1 bg-purple-500 text-white text-sm rounded hover:bg-purple-600 transition-colors"
                  onClick={() => handleQuickCommand('/formal')}
                >
                  🎩 Formal
                </button>
                <button 
                  className="px-3 py-1 bg-green-300 text-sm rounded hover:bg-green-400 transition-colors"
                  onClick={() => handleQuickCommand('/retranslate')}
                >
                  🔄 Retranslate
                </button>
              </div>
            </div>
          )}

          {/* Quick Commands Panel for embedded mode */}
          {showCommands && (
            <div className="border-t border-gray-200 p-4 bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <span className="font-medium text-gray-700">Quick Commands</span>
                <button 
                  className="text-gray-500 hover:text-gray-700 text-lg"
                  onClick={() => setShowCommands(false)}
                >
                  ×
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {quickCommands.map((cmd, index) => (
                  <button
                    key={index}
                    className="p-3 border border-gray-200 rounded-md hover:bg-white hover:border-blue-300 transition-colors text-left"
                    onClick={() => handleQuickCommand(cmd.command)}
                    title={cmd.description}
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">{cmd.icon}</span>
                      <span className="text-blue-600 font-mono text-sm">{cmd.command}</span>
                    </div>
                    <div className="text-xs text-gray-600 mt-1">{cmd.description}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Suggestions for first-time users in embedded mode */}
          {messages.length === 1 && !selectedText && (
            <div className="border-t border-gray-200 p-4 bg-yellow-50">
              <p className="text-sm text-gray-700 mb-3">Try these quick commands:</p>
              <div className="flex flex-wrap gap-2">
                <button
                  className="px-3 py-1 bg-yellow-500 text-white text-sm rounded hover:bg-yellow-600 transition-colors"
                  onClick={() => handleQuickCommand('/format')}
                >
                  📝 Improve formatting
                </button>
                <button
                  className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors"
                  onClick={() => handleQuickCommand('/grammar')}
                >
                  ✏️ Fix grammar
                </button>
                <button
                  className="px-3 py-1 bg-green-300 text-sm rounded hover:bg-green-400 transition-colors"
                  onClick={() => setCurrentInput('Make this translation more natural and fluent')}
                >
                  🎯 Make more natural
                </button>
              </div>
            </div>
          )}

          {/* Input for embedded mode */}
          <div className="border-t border-gray-200 p-4 bg-white">
            <div className="relative">
              <textarea
                ref={inputRef}
                value={currentInput}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder={
                  selectedText 
                    ? "Describe what you want to change about the selected text..." 
                    : "Ask me to improve the content or type / for commands..."
                }
                rows={2}
                disabled={isLoading}
                className="w-full p-3 pr-20 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                autoFocus={isEmbedded}
                tabIndex={0}
              />
              <div className="absolute right-2 top-0 h-full flex items-center space-x-1">
                <button
                  className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                  onClick={() => setShowCommands(!showCommands)}
                  title="Show quick commands"
                  disabled={isLoading}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  ⚡
                </button>
                <button
                  onClick={handleSendMessage}
                  disabled={!currentInput.trim() || isLoading}
                  className="p-2 text-white bg-blue-500 rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {isLoading ? '⏳' : '➤'}
                </button>
              </div>
            </div>
            <div className="text-xs text-gray-500 mt-2">
              {selectedText 
                ? `Working with selection: "${selectedText.substring(0, 30)}${selectedText.length > 30 ? '...' : ''}"`
                : 'Press Enter to send • Type / for commands • Ctrl+Z to undo'
              }
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Regular floating widget mode
  return (
    <div ref={widgetRef} className={getPositionClasses()}>
      {/* Minimized State - Floating Button */}
      {isMinimized && (
        <button 
          className={`relative w-14 h-14 rounded-full shadow-lg transition-all duration-300 ${
            selectedText ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
          } ${hasNewMessage ? 'animate-pulse' : ''}`}
          onClick={toggleWidget}
          title={selectedText ? `${title} - Text Selected` : title}
        >
          <span className="text-white text-xl">💬</span>
          {hasNewMessage && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
              <span className="w-2 h-2 bg-white rounded-full"></span>
            </span>
          )}
          {selectedText && (
            <span className="absolute -bottom-1 -right-1 w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center text-sm">
              ✏️
            </span>
          )}
        </button>
      )}

      {/* Expanded State - Chat Interface */}
      {!isMinimized && isOpen && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg flex flex-col max-h-[95vh] overflow-y-auto mt-1">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-blue-50 flex-shrink-0">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">💬</span>
              <div>
                <h4 className="font-semibold text-gray-800">{title}</h4>
                {documentId && (
                  <span className="text-xs text-gray-500 font-mono">Doc: {documentId.substring(0, 8)}...</span>
                )}
                {selectedText && (
                  <div className="text-xs text-blue-600 mt-1 flex items-center space-x-1">
                    <span>✏️</span>
                    <span>Text Selected ({selectedText.length} chars)</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button 
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                onClick={() => setIsMinimized(true)}
                title="Minimize"
              >
                ─
              </button>
              <button 
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                onClick={closeWidget}
                title="Close"
              >
                ×
              </button>
            </div>
          </div>

          {/* Selection Info Panel */}
          {selectedText && (
            <div className="border-b border-gray-200 p-4 bg-blue-50">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <span className="text-lg">✏️</span>
                  <span className="font-medium text-gray-700">Selected Text</span>
                </div>
                <span className="text-sm text-gray-500">({selectedText.length} characters)</span>
              </div>
              <div className="bg-white p-3 rounded-md text-sm text-gray-700 mb-3 italic">
                "{selectedText.substring(0, 100)}{selectedText.length > 100 ? '...' : ''}"
              </div>
              <div className="flex space-x-2">
                <button 
                  className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors"
                  onClick={() => handleQuickCommand('/grammar')}
                  title="Fix grammar in selection"
                >
                  ✏️ Grammar
                </button>
                <button 
                  className="px-3 py-1 bg-purple-500 text-white text-sm rounded hover:bg-purple-600 transition-colors"
                  onClick={() => handleQuickCommand('/formal')}
                  title="Make selection more formal"
                >
                  🎩 Formal
                </button>
                <button 
                  className="px-3 py-1 bg-green-300 text-sm rounded hover:bg-green-400 transition-colors"
                  onClick={() => handleQuickCommand('/retranslate')}
                  title="Retranslate selection"
                >
                  🔄 Retranslate
                </button>
              </div>
            </div>
          )}

          {/* Quick Commands Panel */}
          {showCommands && (
            <div className="border-b border-gray-200 p-4 bg-gray-50 flex-shrink-0">
              <div className="flex items-center justify-between mb-3">
                <span className="font-medium text-gray-700">Quick Commands</span>
                <button 
                  className="text-gray-500 hover:text-gray-700 text-lg"
                  onClick={() => setShowCommands(false)}
                >
                  ×
                </button>
              </div>
              <div className="grid grid-cols-1 gap-2">
                {quickCommands.map((cmd, index) => (
                  <button
                    key={index}
                    className="p-3 border border-gray-200 rounded-md hover:bg-white hover:border-blue-300 transition-colors text-left"
                    onClick={() => handleQuickCommand(cmd.command)}
                    title={cmd.description}
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">{cmd.icon}</span>
                      <span className="text-blue-600 font-mono text-sm">{cmd.command}</span>
                    </div>
                    <div className="text-xs text-gray-600 mt-1">{cmd.description}</div>
                  </button>
                ))}
              </div>
              <div className="text-xs text-gray-500 mt-3 text-center">
                💡 Type / in the input to see commands
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
            {messages.map((message) => (
              <div key={message.id} className={`flex flex-col ${message.type === 'user' ? 'items-end' : 'items-start'}`}>
                <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${message.type === 'user' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-800'}`}>
                  {message.isLoading ? (
                    <div className="flex items-center space-x-2">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                      <span className="text-sm">Processing...</span>
                    </div>
                  ) : (
                    <>
                      <p className="whitespace-pre-wrap">{message.content}</p>
                      
                      {message.htmlUpdate && message.changes && message.changes.length > 0 && (
                        <div className="mt-3 p-3 bg-white bg-opacity-20 rounded-md">
                          <h5 className="font-semibold text-sm mb-2">Changes made:</h5>
                          <ul className="text-sm space-y-1">
                            {message.changes.map((change, index) => (
                              <li key={index} className="flex items-start">
                                <span className="text-green-600 mr-1">•</span>
                                {change}
                              </li>
                            ))}
                          </ul>
                          {message.selectedText && (
                            <div className="mt-2 p-2 bg-white bg-opacity-30 rounded text-sm">
                              <strong>Focused on:</strong> "{message.selectedText.substring(0, 50)}..."
                            </div>
                          )}
                          {/* FIXED: Only show undo button if we have previous HTML or history */}
                          {(message.previousHtml || currentHistoryIndex > 0) && (
                            <button 
                              className="mt-2 px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
                              onClick={() => handleRevert(message.id)}
                              title="Undo this change"
                            >
                              Undo Changes
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className={`text-xs text-gray-500 mt-1 px-1 ${message.type === 'user' ? 'text-right' : 'text-left'}`}>
                  {formatTimestamp(message.timestamp)}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Suggestions for first-time users */}
          {messages.length === 1 && !selectedText && (
            <div className="border-t border-gray-200 p-4 bg-yellow-50">
              <p className="text-sm text-gray-700 mb-3">Try these quick commands:</p>
              <div className="flex flex-wrap gap-2">
                <button
                  className="px-3 py-1 bg-yellow-500 text-white text-sm rounded hover:bg-yellow-600 transition-colors"
                  onClick={() => handleQuickCommand('/format')}
                >
                  📝 Improve formatting
                </button>
                <button
                  className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors"
                  onClick={() => handleQuickCommand('/grammar')}
                >
                  ✏️ Fix grammar
                </button>
                <button
                  className="px-3 py-1 bg-green-300 text-sm rounded hover:bg-green-400 transition-colors"
                  onClick={() => setCurrentInput('Make this translation more natural and fluent')}
                >
                  🎯 Make more natural
                </button>
              </div>
            </div>
          )}

          {/* Input */}
          <div className="border-t border-gray-200 p-4 bg-white">
            <div className="relative">
              <textarea
                ref={inputRef}
                value={currentInput}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder={
                  selectedText 
                    ? "Describe what you want to change about the selected text..." 
                    : "Ask me to improve the content or type / for commands..."
                }
                rows={1}
                disabled={isLoading}
                className="w-full p-3 pr-20 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                tabIndex={0}
              />
              <div className="absolute right-2 top-2 flex space-x-1">
                <button
                  className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                  onClick={() => setShowCommands(!showCommands)}
                  title="Show quick commands"
                  disabled={isLoading}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  ⚡
                </button>
                <button
                  onClick={handleSendMessage}
                  disabled={!currentInput.trim() || isLoading}
                  className="p-2 text-white bg-blue-500 rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {isLoading ? '⏳' : '➤'}
                </button>
              </div>
            </div>
            <div className="text-xs text-gray-500 mt-2">
              {selectedText 
                ? `Working with selection: "${selectedText.substring(0, 30)}${selectedText.length > 30 ? '...' : ''}"`
                : 'Press Enter to send • Type / for commands • Ctrl+Z to undo'
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatbotWidget;